import React, { useState, useEffect } from "react";
import {
  ArrowLeft, Clock, FileText, MessageSquare, Paperclip,
  Upload, CheckCircle, AlertCircle, HelpCircle, ExternalLink, Wrench
} from "lucide-react";
import { useParams, Link } from "react-router-dom";
import { detectLocale } from "../i18n";

const tstData = {
  en: {
    title: "Technical request tracking",
    not_found: "Request not found. Check the URL or contact support.",
    protocol: "Protocol number",
    status: "Current status",
    timeline: "Timeline",
    messages: "Messages",
    documents: "Pending documents",
    eta: "Estimated response",
    updated: "Last update",
    status_opts: ["Received", "Under review", "Awaiting documents", "Technical evaluation", "Resolved", "Rejected", "Completed", "Cancelled"],
    add_doc: "Send additional document",
    reply: "Reply to ticket",
    talk: "Contact support",
    support_msg: "If any information is missing, you can supplement the request from this screen.",
    back: "Back to request",
    back_support: "Back to support",
    explore_faq: "Explore FAQ",
    no_docs: "No pending documents",
    no_messages: "No messages yet",
    submit_doc: "Submit document",
    cancel: "Cancel",
    doc_sent: "Document sent successfully",
    message_placeholder: "Type your message...",
    send: "Send",
  },
  pt: {
    title: "Acompanhamento de chamado técnico",
    not_found: "Solicitação não encontrada. Verifique a URL ou entre em contato com o suporte.",
    protocol: "Número do protocolo",
    status: "Status atual",
    timeline: "Linha do tempo",
    messages: "Mensagens",
    documents: "Documentos pendentes",
    eta: "Prazo estimado",
    updated: "Última atualização",
    status_opts: ["Recebido", "Em análise", "Aguardando documentos", "Em avaliação técnica", "Resolvido", "Rejeitado", "Concluído", "Cancelado"],
    add_doc: "Enviar documento adicional",
    reply: "Responder ao chamado",
    talk: "Falar com suporte",
    support_msg: "Se faltar alguma informação, você poderá complementar o chamado por esta tela.",
    back: "Voltar para chamado",
    back_support: "Voltar para suporte",
    explore_faq: "Explorar FAQ",
    no_docs: "Nenhum documento pendente",
    no_messages: "Nenhuma mensagem ainda",
    submit_doc: "Enviar documento",
    cancel: "Cancelar",
    doc_sent: "Documento enviado com sucesso",
    message_placeholder: "Digite sua mensagem...",
    send: "Enviar",
  },
  es: {
    title: "Seguimiento de llamado técnico",
    not_found: "Solicitud no encontrada. Verifique la URL o contacte soporte.",
    protocol: "Número de protocolo",
    status: "Estado actual",
    timeline: "Línea de tiempo",
    messages: "Mensajes",
    documents: "Documentos pendientes",
    eta: "Plazo estimado",
    updated: "Última actualización",
    status_opts: ["Recibido", "En análisis", "Esperando documentos", "Evaluación técnica", "Resuelto", "Rechazado", "Completado", "Cancelado"],
    add_doc: "Enviar documento adicional",
    reply: "Responder al ticket",
    talk: "Hablar con soporte",
    support_msg: "Si falta alguna información, puede complementar la solicitud desde esta pantalla.",
    back: "Volver al llamado",
    back_support: "Volver a soporte",
    explore_faq: "Explorar FAQ",
    no_docs: "Sin documentos pendientes",
    no_messages: "Sin mensajes aún",
    submit_doc: "Enviar documento",
    cancel: "Cancelar",
    doc_sent: "Documento enviado exitosamente",
    message_placeholder: "Escriba su mensaje...",
    send: "Enviar",
  },
};

const statusColors = {
  0: "bg-sky-500",
  1: "bg-blue-500",
  2: "bg-amber-500",
  3: "bg-purple-500",
  4: "bg-emerald-500",
  5: "bg-red-500",
  6: "bg-slate-500",
  7: "bg-slate-400",
};

export default function TechnicalSupportTracking() {
  const { id } = useParams();
  const locale = detectLocale();
  const t = tstData[locale] || tstData.en;

  const [request, setRequest] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("timeline");
  const [showDocForm, setShowDocForm] = useState(false);
  const [docUploaded, setDocUploaded] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const requests = JSON.parse(localStorage.getItem("sharkx_tech_requests") || "[]");
    const found = requests.find((r) => r.protocol === id);
    if (found) {
      setRequest(found);
      if (found.messages) setMessages(found.messages);
    } else {
      setNotFound(true);
    }
  }, [id]);

  if (notFound) {
    return (
      <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen">
        <div className="max-w-[720px] mx-auto px-6 md:px-12">
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <AlertCircle className="mx-auto text-slate-300 mb-4" size={48} />
            <h1 className="font-serif-display text-2xl text-slate-900 mb-3">{t.not_found}</h1>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
              <Link to="/support/technical-request"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                <Wrench size={16} /> {t.back}
              </Link>
              <Link to="/support"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
                <ArrowLeft size={16} /> {t.back_support}
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!request) return null;

  const statusIdx = request.status || 0;
  const statusLabel = t.status_opts[statusIdx] || t.status_opts[0];
  const statusColor = statusColors[statusIdx] || statusColors[0];
  const timeline = request.timeline || [];

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    const newMsg = {
      id: Date.now(),
      from: "user",
      text: messageText,
      date: new Date().toISOString(),
    };
    const updated = [...messages, newMsg];
    setMessages(updated);
    setMessageText("");
    const requests = JSON.parse(localStorage.getItem("sharkx_tech_requests") || "[]");
    const idx = requests.findIndex((r) => r.protocol === id);
    if (idx >= 0) {
      requests[idx].messages = updated;
      localStorage.setItem("sharkx_tech_requests", JSON.stringify(requests));
    }
  };

  const handleSendDoc = () => {
    setDocUploaded(true);
    setTimeout(() => {
      setShowDocForm(false);
      setDocUploaded(false);
    }, 2000);
  };

  return (
    <main className="pt-32 pb-32 bg-[#f6f6f3] min-h-screen" data-testid="technical-support-tracking-page">
      <div className="max-w-[720px] mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <p className="text-xs font-ui uppercase tracking-[0.15em] text-slate-500 mb-1">{t.protocol}</p>
              <p className="text-lg font-mono font-bold text-slate-900">{request.protocol}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${statusColor}`} />
              <span className="text-sm font-medium text-slate-700">{statusLabel}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500 mb-1">{t.status}</p>
              <p className="text-sm font-medium text-slate-700">{statusLabel}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">{t.eta}</p>
              <p className="text-sm font-medium text-slate-700">
                {locale === "pt" ? "2-5 dias úteis" : locale === "es" ? "2-5 días hábiles" : "2-5 business days"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">{t.updated}</p>
              <p className="text-sm font-medium text-slate-700">
                {new Date(request.createdAt).toLocaleDateString(
                  locale === "pt" ? "pt-BR" : locale === "es" ? "es-ES" : "en-US",
                  { day: "2-digit", month: "short", year: "numeric" }
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-lg p-1">
          {[
            { key: "timeline", label: t.timeline, icon: Clock },
            { key: "messages", label: t.messages, icon: MessageSquare },
            { key: "documents", label: t.documents, icon: FileText },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-sky-500 text-white"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <tab.icon size={14} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Timeline Tab */}
        {activeTab === "timeline" && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8">
            <h3 className="font-semibold text-slate-900 mb-6">{t.timeline}</h3>
            {timeline.length === 0 ? (
              <p className="text-sm text-slate-500">{locale === "pt" ? "Nenhum evento ainda" : locale === "es" ? "Sin eventos aún" : "No events yet"}</p>
            ) : (
              <div className="space-y-0">
                {timeline.map((event, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${statusColors[event.status] || "bg-slate-300"} flex-shrink-0 mt-1`} />
                      {i < timeline.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                    </div>
                    <div className="pb-6">
                      <p className="text-sm font-medium text-slate-700">{event.text}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(event.date).toLocaleDateString(
                          locale === "pt" ? "pt-BR" : locale === "es" ? "es-ES" : "en-US",
                          { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === "messages" && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8">
            <h3 className="font-semibold text-slate-900 mb-6">{t.messages}</h3>

            {messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="mx-auto text-slate-300 mb-3" size={32} />
                <p className="text-sm text-slate-500">{t.no_messages}</p>
              </div>
            ) : (
              <div className="space-y-4 mb-6">
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                      msg.from === "user" ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-700"
                    }`}>
                      <p className="text-sm">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.from === "user" ? "text-sky-200" : "text-slate-400"}`}>
                        {new Date(msg.date).toLocaleDateString(
                          locale === "pt" ? "pt-BR" : locale === "es" ? "es-ES" : "en-US",
                          { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Message input */}
            <div className="border-t border-slate-100 pt-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  placeholder={t.message_placeholder}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                />
                <button
                  onClick={handleSendMessage}
                  className="px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors"
                >
                  {t.send}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold text-slate-900">{t.documents}</h3>
              <button
                onClick={() => setShowDocForm(!showDocForm)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-medium transition-colors"
              >
                <Upload size={14} /> {t.add_doc}
              </button>
            </div>

            {showDocForm && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
                {docUploaded ? (
                  <div className="flex items-center gap-3 text-emerald-600">
                    <CheckCircle size={20} />
                    <span className="text-sm font-medium">{t.doc_sent}</span>
                  </div>
                ) : (
                  <>
                    <div
                      className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center mb-4 cursor-pointer hover:border-sky-400 transition-colors"
                    >
                      <Upload className="mx-auto text-slate-400 mb-2" size={24} />
                      <p className="text-sm text-slate-500">{locale === "pt" ? "Clique ou arraste arquivos aqui" : locale === "es" ? "Haga clic o arrastre archivos aquí" : "Click or drag files here"}</p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setShowDocForm(false)}
                        className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-medium hover:border-slate-400 transition-colors"
                      >
                        {t.cancel}
                      </button>
                      <button
                        onClick={handleSendDoc}
                        className="px-4 py-2 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors"
                      >
                        {t.submit_doc}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {request.uploads && Object.keys(request.uploads).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(request.uploads).map(([category, files]) => (
                  files.map((file, i) => (
                    <div key={`${category}-${i}`} className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-3">
                      <Paperclip size={16} className="text-slate-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-700 truncate">{file.name}</p>
                        <p className="text-[10px] text-slate-400">{category} • {(file.size / 1024).toFixed(0)} KB</p>
                      </div>
                    </div>
                  ))
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="mx-auto text-slate-300 mb-3" size={32} />
                <p className="text-sm text-slate-500">{t.no_docs}</p>
              </div>
            )}
          </div>
        )}

        {/* Support message */}
        <div className="mt-6 bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
          <HelpCircle className="text-sky-500 flex-shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-slate-600">{t.support_msg}</p>
        </div>

        {/* Bottom links */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/support/technical-request"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
            <Wrench size={16} /> {t.back}
          </Link>
          <Link to="/support"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
            <ArrowLeft size={16} /> {t.back_support}
          </Link>
          <Link to="/faq"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 hover:border-slate-400 text-slate-600 text-sm font-ui font-semibold uppercase tracking-wider transition-colors">
            <ExternalLink size={16} /> {t.explore_faq}
          </Link>
        </div>

      </div>
    </main>
  );
}
